import { ShieldCheck } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { Footer } from "@/components/Footer";

const SECTIONS = [
  {
    id: "introduction", n: 1, title: "Introduction",
    blocks: [
      { p: 'Welcome to Z2Infinity ("Z2Infinity," "we," "our," or "us").' },
      { p: "We respect your privacy and are committed to protecting the personal information of students, parents, educators, and other individuals who use our platform." },
      { p: "This Privacy Policy explains how we collect, use, store, process, share, and protect personal information when you access our website, create an account, or use our educational services." },
      { p: "Z2Infinity provides AI-powered educational tools designed to support students preparing for competitive examinations, including the Joint Entrance Examination (JEE Main and Advanced) and the National Eligibility cum Entrance Test (NEET)." },
      { p: "By using Z2Infinity, you acknowledge that you have been provided with this Privacy Policy. Where consent is legally required, we will request it separately." },
    ],
  },
  {
    id: "information-we-collect", n: 2, title: "Information We Collect",
    blocks: [
      { p: "Depending on the services and features you use, we may collect the following categories of information." },
      { sub: "2.1 Account and Personal Information" },
      { p: "When you register or interact with Z2Infinity, we may collect:" },
      { ul: ["Full name or preferred display name", "Email address", "Mobile phone number", "Login credentials or authentication identifiers", "Class, grade, or academic level", "Examination preferences, such as JEE or NEET", "Account preferences and settings", "Parent or guardian contact information, where applicable"] },
      { p: "We collect only information reasonably necessary to provide the relevant services." },
      { sub: "2.2 Educational and Learning Information" },
      { p: "To provide personalized learning experiences, we may process:" },
      { ul: ["Questions attempted and answers submitted", "Practice test results and scores", "Subject-wise and topic-wise performance", "Learning progress and study history", "Time spent on practice activities", "Identified strengths and areas for improvement", "AI-generated study recommendations", "Learning preferences and selected study goals"] },
      { p: "This information helps us provide educational feedback and improve learning experiences." },
      { sub: "2.3 AI Interactions" },
      { p: "If you use AI-powered features, we may process:" },
      { ul: ["Questions and prompts submitted to AI assistants", "Explanations, answers, and feedback generated by AI systems", "Conversation history, where the feature supports it", "Uploaded educational content, if supported", "Information required to personalize educational assistance"] },
      { p: "AI interactions may be processed by third-party AI technology providers acting on our behalf." },
      { p: "We do not intentionally require students to submit sensitive personal information through AI conversations." },
      { p: "Users should avoid sharing passwords, financial information, government identification numbers, or other sensitive personal details in AI prompts." },
      { sub: "2.4 Technical and Usage Information" },
      { p: "When you access our website, certain technical information may be collected automatically, including:" },
      { ul: ["IP address", "Browser and device type", "Operating system", "Approximate geographic location derived from IP address", "Pages visited and features accessed", "Session information", "Error logs and diagnostic information", "Cookies and similar technologies"] },
      { p: "This information helps us maintain platform performance, reliability, and security." },
      { sub: "2.5 Payment Information" },
      { p: "If Z2Infinity offers paid subscriptions or services, payments may be processed by authorized third-party payment providers." },
      { p: "We may receive transaction identifiers, payment status, subscription details, and billing information needed to administer purchases." },
      { p: "Full payment card details are handled by the applicable payment provider rather than being stored directly by Z2Infinity, provided that our payment integration is configured accordingly." },
    ],
  },
  {
    id: "how-we-use", n: 3, title: "How We Use Your Information",
    blocks: [
      { p: "We may use collected information to:" },
      { ul: ["Create and manage user accounts.", "Provide access to educational content, practice tests, and learning tools.", "Personalize learning experiences based on student performance.", "Generate AI-powered explanations and educational recommendations.", "Track learning progress and provide performance analytics.", "Maintain and improve platform functionality.", "Respond to inquiries and support requests.", "Send important account, security, and service communications.", "Prevent unauthorized access, fraud, misuse, and security incidents.", "Comply with applicable legal obligations."] },
      { p: "Where required by applicable law, we obtain consent or rely on another permitted legal basis before processing personal information." },
    ],
  },
  {
    id: "ai-automated", n: 4, title: "Artificial Intelligence and Automated Processing",
    blocks: [
      { p: "Z2Infinity may use artificial intelligence and machine learning technologies to deliver educational assistance, analyze learning patterns, and provide personalized recommendations." },
      { p: "AI-generated educational content may contain inaccuracies or incomplete explanations and should be verified when necessary." },
      { p: "Student performance data may be used to personalize learning experiences within the platform." },
      { p: "We do not use identifiable student conversations or personal educational records to train third-party general-purpose AI models unless we have established an appropriate lawful basis, provided required notices, and obtained any necessary consent." },
      { p: "We may use aggregated or appropriately de-identified information to evaluate and improve educational services, subject to applicable law." },
    ],
  },
  {
    id: "childrens-privacy", n: 5, title: "Children's and Students' Privacy",
    blocks: [
      { p: "Z2Infinity recognizes that many students preparing for JEE and NEET may be under 18 years of age." },
      { p: "We are committed to implementing appropriate safeguards for children and younger users." },
      { p: "Where required by applicable law, we will:" },
      { ul: ["Obtain verifiable parental or guardian consent before processing a child's personal information.", "Provide clear and age-appropriate privacy information.", "Limit the collection of children's personal information to what is necessary for the educational service.", "Restrict access to student information to authorized individuals and service providers.", "Avoid targeted advertising directed at children where prohibited.", "Avoid prohibited tracking or behavioral monitoring of children.", "Provide parents or guardians with mechanisms to exercise applicable privacy rights."] },
      { p: "The specific requirements may vary depending on the student's location and applicable law." },
      { p: "If you believe a child has provided personal information without legally required consent, please contact us so we can investigate and take appropriate action." },
    ],
  },
  {
    id: "cookies", n: 6, title: "Cookies and Similar Technologies",
    blocks: [
      { p: "Z2Infinity may use cookies and similar technologies for purposes such as:" },
      { ul: ["Maintaining secure login sessions", "Remembering user preferences", "Supporting essential website functionality", "Understanding website performance", "Identifying technical problems", "Improving user experience"] },
      { p: "Where non-essential analytics or marketing cookies are used, we will provide appropriate notices and consent choices where legally required." },
      { p: "Users can also manage cookies through their browser settings. Disabling essential cookies may affect website functionality." },
    ],
  },
  {
    id: "sharing", n: 7, title: "Information Sharing and Disclosure",
    blocks: [
      { p: "We do not sell personal information in exchange for money." },
      { p: "We may share limited personal information with trusted third parties where necessary to operate our services, including:" },
      { ul: ["Cloud and hosting providers: To host the website, store information, and maintain infrastructure.", "AI technology providers: To process educational prompts and generate AI-powered responses.", "Authentication providers: To support account registration and secure login.", "Payment processors: To process subscriptions and payments, if applicable.", "Analytics and technical service providers: To maintain platform performance and troubleshoot technical issues.", "Legal and regulatory authorities: Where disclosure is required by applicable law or valid legal process.", "Business successors: In connection with a merger, acquisition, restructuring, or transfer of business assets, subject to applicable privacy obligations."] },
      { p: "We require appropriate contractual and security protections from service providers that process personal information on our behalf." },
    ],
  },
  {
    id: "security", n: 8, title: "Data Security",
    blocks: [
      { p: "We use reasonable technical and organizational safeguards designed to protect personal information against unauthorized access, disclosure, alteration, misuse, and loss." },
      { p: "Depending on the relevant systems, these safeguards may include encrypted connections, access controls, secure authentication, monitoring, and restricted administrative permissions." },
      { p: "However, no internet-based system can guarantee absolute security." },
      { p: "Users are responsible for protecting their login credentials and notifying us if they suspect unauthorized access to their accounts." },
    ],
  },
  {
    id: "retention", n: 9, title: "Data Retention",
    blocks: [
      { p: "We retain personal information only for as long as reasonably necessary to fulfill the purposes described in this Privacy Policy, comply with applicable laws, resolve disputes, and maintain platform security." },
      { p: "Account and educational progress information may be retained while an account remains active." },
      { p: "When an account is deleted, associated personal information will be deleted or de-identified according to our retention procedures, except where continued retention is legally required or otherwise permitted." },
      { p: "Backup copies may remain temporarily until they are removed through established backup retention cycles." },
    ],
  },
  {
    id: "your-rights", n: 10, title: "Your Privacy Rights",
    blocks: [
      { p: "Depending on your location and applicable law, you may have rights to:" },
      { ul: ["Request access to personal information we maintain about you.", "Correct inaccurate or incomplete personal information.", "Request deletion of personal information.", "Withdraw consent where processing is based on consent.", "Request information about how your personal information is processed.", "Raise privacy concerns or complaints.", "Exercise other rights provided under applicable data protection laws."] },
      { p: "Parents or legal guardians may exercise applicable rights on behalf of eligible children." },
      { p: "To submit a privacy request, contact us using the details in Section 15." },
      { p: "We may need to verify your identity before fulfilling a request." },
    ],
  },
  {
    id: "international", n: 11, title: "International Data Processing",
    blocks: [
      { p: "Z2Infinity may use technology infrastructure and service providers located in different countries." },
      { p: "As a result, personal information may be processed outside the country where a user resides." },
      { p: "Where applicable, we will comply with relevant restrictions and safeguards governing international transfers of personal information." },
    ],
  },
  {
    id: "third-party", n: 12, title: "Third-Party Websites and Services",
    blocks: [
      { p: "Our platform may contain links to third-party educational resources, examination authorities, external websites, or other services." },
      { p: "These third parties may have their own privacy practices." },
      { p: "Z2Infinity is not responsible for the privacy policies or data-handling practices of independently operated third-party websites." },
      { p: "We encourage users to review the privacy policies of external services before sharing personal information." },
    ],
  },
  {
    id: "india-dpdp", n: 13, title: "Indian Data Protection Requirements",
    blocks: [
      { p: "For users located in India, Z2Infinity intends to handle personal information in accordance with applicable provisions of the Digital Personal Data Protection Act, 2023, and relevant rules as they become enforceable." },
      { p: "Where legally required, we will provide appropriate notices, establish consent and withdrawal mechanisms, facilitate applicable data principal rights, and implement additional safeguards for children's personal information." },
      { p: "Users may contact us to raise privacy grievances or request assistance with their personal information." },
      { p: "Nothing in this Privacy Policy limits rights granted to individuals under applicable law." },
    ],
  },
  {
    id: "changes", n: 14, title: "Changes to This Privacy Policy",
    blocks: [
      { p: "We may update this Privacy Policy periodically to reflect changes in our educational services, technology, legal obligations, or data-processing practices." },
      { p: "When we make material changes, we will provide appropriate notice through the website, account notifications, or other reasonable communication channels." },
      { p: "The effective date displayed at the beginning of this policy indicates when the current version became effective." },
    ],
  },
  {
    id: "contact", n: 15, title: "Contact Us",
    blocks: [
      { p: "For questions, privacy concerns, data access requests, or deletion requests, please contact:" },
      { contact: true },
      { p: "We will review privacy-related requests and respond within the timeframes required by applicable law." },
    ],
  },
];

const Block = ({ block }) => {
  if (block.p) return <p className="mt-3 text-[15px] leading-relaxed text-foreground/80">{block.p}</p>;
  if (block.sub) return <h3 className="mt-6 font-heading text-base font-semibold text-foreground">{block.sub}</h3>;
  if (block.ul)
    return (
      <ul className="mt-3 space-y-2">
        {block.ul.map((item, i) => (
          <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-foreground/80">
            <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/60" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  if (block.contact)
    return (
      <div className="mt-4 rounded-2xl border border-border bg-secondary/40 p-5 text-[15px] leading-relaxed text-foreground/80">
        <div className="font-semibold text-foreground">Z2Infinity — Privacy Support</div>
        <div className="mt-2">Website: <a href="https://z2infinity.com/" className="text-primary hover:underline">https://z2infinity.com/</a></div>
        <div>Email: <span className="text-muted-foreground">[Insert verified privacy or support email address]</span></div>
        <div>Legal Entity: <span className="text-muted-foreground">[Insert registered company or operator name]</span></div>
        <div>Business Address: <span className="text-muted-foreground">[Insert applicable business or registered address]</span></div>
      </div>
    );
  return null;
};

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-border bg-zinc-950">
        <div className="absolute inset-0 bg-[radial-gradient(60%_120%_at_50%_0%,rgba(59,130,246,0.18),transparent)]" />
        <div className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="flex items-center gap-2">
            <BrandMark className="h-9" />
            <span className="font-heading text-lg font-bold tracking-tight text-white">Z2Infinity</span>
          </div>
          <span className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium tracking-wide text-white/80 backdrop-blur">
            <ShieldCheck className="h-3.5 w-3.5" /> Your privacy matters
          </span>
          <h1 className="mt-4 font-heading text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">Privacy Policy</h1>
          <p className="mt-3 text-base text-white/70">AI-Powered JEE &amp; NEET Preparation</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/60">
            <span>Effective Date: October 17, 2026</span>
            <a href="https://z2infinity.com/" className="text-blue-300 hover:underline">https://z2infinity.com/</a>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        {/* Table of contents */}
        <nav className="mb-12 rounded-2xl border border-border bg-card p-6" data-testid="privacy-toc">
          <h2 className="mb-4 text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">On this page</h2>
          <ol className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-sm text-foreground/70 transition-colors hover:text-primary">
                  {s.n}. {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        <article>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24 border-t border-border py-9 first:border-t-0 first:pt-0">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                <span className="text-primary">{s.n}.</span> {s.title}
              </h2>
              {s.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </section>
          ))}
        </article>

        {/* Footer disclaimer */}
        <div className="mt-10 border-t border-border pt-8 text-sm text-muted-foreground">
          <p>© 2026 Z2Infinity. All rights reserved.</p>
          <p className="mt-2 leading-relaxed">
            Z2Infinity is an independent educational technology platform. References to JEE, NEET, NTA, IITs, or other
            examination authorities do not imply official affiliation or endorsement unless expressly stated.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
